import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/samples', label: 'Sample Videos' },
    { path: '/services', label: 'Services' },
    { path: '/how-it-works', label: 'How It Works' },
    { path: '/pricing', label: 'Pricing' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-black/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-40">
          {/* Logo */}
          <Link to="/" className="flex items-center group animate-header-1">
            <img 
              src="https://customer-assets.emergentagent.com/job_slideshow-studio-5/artifacts/3f84c0r9_file_00000000e84c71f4bc5168c8fafe2f26.png" 
              alt="Visual Creationz" 
              className="h-14 md:h-36 w-auto transition-transform duration-300 group-hover:scale-105 logo-glow-pulse"
            />
            {/* Desktop-only tagline - hidden on mobile/tablet to keep header clean */}
            <span className="hidden xl:flex flex-col ml-1 -mt-1 leading-tight">
              <span className="text-[11px] tracking-[0.35em] uppercase text-gray-400">Cinematic</span>
              <span className="text-[11px] tracking-[0.35em] uppercase text-gray-400">Visual</span>
              <span className="text-[11px] tracking-[0.35em] uppercase bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent font-semibold">Storytelling</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8 animate-header-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link relative text-sm font-medium transition-all duration-300 ${
                  location.pathname === link.path 
                    ? 'text-cyan-400' 
                    : 'text-white hover:text-cyan-400'
                }`}
              >
                {link.label}
                {location.pathname === link.path && (
                  <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-cyan-400 to-blue-500"></span>
                )}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <Link
            to="/pricing"
            className="hidden lg:inline-flex items-center justify-center px-6 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-full hover:from-amber-600 hover:to-amber-700 active:scale-95 transition-all duration-300 shadow-lg hover:shadow-amber-500/50 hover:scale-105 animate-header-3"
          >
            Order Now
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white p-2 hover:text-cyan-400 active:scale-90 transition-all animate-header-2"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed top-20 md:top-40 left-0 right-0 bg-black backdrop-blur-md border-t border-white/10 shadow-2xl z-40 max-h-[calc(100vh-5rem)] overflow-y-auto">
            <nav className="flex flex-col py-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-6 py-3 text-base transition-all duration-300 ${
                    location.pathname === link.path
                      ? 'text-cyan-400 bg-white/5'
                      : 'text-white hover:text-cyan-400 hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/pricing"
                onClick={() => setIsMobileMenuOpen(false)}
                className="mx-6 mt-4 py-3 text-center bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-full hover:from-amber-600 hover:to-amber-700 active:scale-95 transition-all duration-300"
              >
                Order Now
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
